export const DEFAULT_DAY = { off: false, morningStart: "09:00", eveningStart: "17:00" };
export const OFF_DAY = { off: true, morningStart: "09:00", eveningStart: "17:00" };
export const DEFAULT_WEEKLY_SCHEDULE = {
    monday: { ...DEFAULT_DAY },
    tuesday: { ...DEFAULT_DAY },
    wednesday: { ...DEFAULT_DAY },
    thursday: { ...DEFAULT_DAY },
    friday: { ...DEFAULT_DAY },
    saturday: { ...DEFAULT_DAY },
    sunday: { ...OFF_DAY },
};
/**
 * Coerce a stored weekly_schedule value into a valid WeeklySchedule object.
 *
 * The live DB historically held this jsonb DOUBLE-ENCODED as a string
 * (`"\"{...}\""` — jsonb_typeof = 'string'), which made `weeklySchedule.monday`
 * undefined and isWorkingDay() false for EVERY date (rebook 422
 * target_not_working, review_date_not_working, slot-block move validation).
 * This helper accepts both shapes plus per-entry nulls and returns a complete
 * 7-day schedule, falling back to DEFAULT_WEEKLY_SCHEDULE per missing day.
 *
 * Call this at every read boundary (Worker routes + web queries) — never
 * index the raw settings value directly.
 */
export function normalizeWeeklySchedule(raw) {
    let obj = raw;
    // Unwrap double-encoded strings (up to 2 layers of JSON stringifying).
    for (let i = 0; i < 2 && typeof obj === "string"; i++) {
        try {
            obj = JSON.parse(obj);
        }
        catch {
            break; // plain string that isn't JSON — fall through to defaults
        }
    }
    const src = (obj && typeof obj === "object" && !Array.isArray(obj) ? obj : {});
    const out = {};
    for (const key of Object.keys(DEFAULT_WEEKLY_SCHEDULE)) {
        const day = src[key];
        out[key] = {
            off: typeof day?.off === "boolean" ? day.off : DEFAULT_WEEKLY_SCHEDULE[key].off,
            morningStart: typeof day?.morningStart === "string" ? day.morningStart : DEFAULT_WEEKLY_SCHEDULE[key].morningStart,
            eveningStart: typeof day?.eveningStart === "string" ? day.eveningStart : DEFAULT_WEEKLY_SCHEDULE[key].eveningStart,
        };
    }
    return out;
}
const WEEKDAY_KEYS = [
    "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
];
function weekdayKey(d) {
    return WEEKDAY_KEYS[d.getDay()];
}
/** The config a date resolves to: among APPLIED configs (appliedAt set),
 * the one with the greatest effective_from <= date. Historical dates BEFORE
 * the earliest applied config's effectiveFrom resolve to the EARLIEST
 * applied config (callers always get a usable slot list — past-date pages
 * must keep working). Returns null only when NO applied config exists at
 * all. effectiveFrom comparison is on the YYYY-MM-DD string
 * (lexicographic == chronological for ISO dates). */
export function resolveSlotConfig(configs, date) {
    const applied = configs.filter((c) => c.appliedAt !== undefined && c.appliedAt !== null);
    if (applied.length === 0)
        return null;
    const ds = dateKey(date);
    let best = null;
    for (const c of applied) {
        if (c.effectiveFrom <= ds && (!best || c.effectiveFrom > best.effectiveFrom)) {
            best = c;
        }
    }
    if (best)
        return best;
    // Date is before every applied config's effectiveFrom (historical) —
    // fall back to the earliest applied config so callers always have slots.
    return applied.reduce((min, c) => (c.effectiveFrom < min.effectiveFrom ? c : min));
}
/** TRUE ONLY when `now` (IST) is past the slot's checkin_end — i.e. the
 * patient arrived after the check-in window closed. Early arrivals (before
 * checkin_start) are NOT late — they check in to their own slot normally.
 * Both `now` and the def's times are IST; comparison is on 'HH:mm' strings
 * (lexicographic == chronological for zero-padded times). */
export function isLateForCheckin(now, def) {
    const hhmm = timeKeyIST(now);
    return hhmm > def.checkinEnd;
}
/** Next slot (by sortOrder) on `date` whose check-in window is open or still
 * ahead (now <= checkinEnd), not blocked (isSlotBlocked), and with remaining
 * capacity (bookedCount < effectiveMaxAppts). Used by the late-push flow:
 * a late patient is pushed to the first such slot. `now` is IST. */
export function nextAvailableSlot(date, defs, blocks, bookedCounts, now) {
    const sorted = [...defs].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const def of sorted) {
        if (isSlotBlocked(date, def.key, blocks))
            continue;
        const hhmm = timeKeyIST(now);
        if (hhmm > def.checkinEnd)
            continue; // window already closed
        const max = effectiveMaxAppts(date, def.key, blocks, def.maxAppts);
        if (max <= 0)
            continue;
        const booked = bookedCounts[def.key] ?? 0;
        if (booked < max)
            return def;
    }
    return null;
}
/** A date is working if its weekday is not marked off in the weekly schedule.
 * Falls back to DEFAULT_WEEKLY_SCHEDULE when the schedule is missing, a
 * double-encoded string, or otherwise not a plain object (defensive — see
 * normalizeWeeklySchedule; callers should pass a normalized schedule). */
export function isWorkingDay(date, weeklySchedule) {
    const day = weeklySchedule?.[weekdayKey(date)];
    if (!day || typeof day !== "object")
        return isWorkingDay(date, DEFAULT_WEEKLY_SCHEDULE);
    return !day.off;
}
/**
 * A date is whole-day-blocked if any slot_blocks row has that date with
 * slot=null AND max_override is 0 or NULL (legacy whole-day rows carried no
 * max columns — NULL is treated as blocked, preserving the legacy semantics).
 */
export function isWholeDayBlocked(date, slotBlocks) {
    const ds = dateKey(date);
    const wholeDay = slotBlocks.find((b) => b.date === ds && b.slot === null);
    if (!wholeDay)
        return false;
    return wholeDay.maxOverride === undefined || wholeDay.maxOverride === null || wholeDay.maxOverride === 0;
}
/**
 * Resolve the effective max confirmed appointments for a (date, slot key).
 * Precedence: per-slot override > whole-day override > global default.
 * Returns 0 if the slot is fully blocked.
 */
export function effectiveMaxAppts(date, slotKey, slotBlocks, globalDefault) {
    const ds = dateKey(date);
    const wholeDay = slotBlocks.find((b) => b.date === ds && b.slot === null);
    const perSlot = slotBlocks.find((b) => b.date === ds && b.slot === slotKey);
    // Per-slot override wins if set (including 0 = blocked).
    if (perSlot && perSlot.maxOverride !== undefined && perSlot.maxOverride !== null) {
        return perSlot.maxOverride;
    }
    if (wholeDay && wholeDay.maxOverride !== undefined && wholeDay.maxOverride !== null) {
        return wholeDay.maxOverride;
    }
    return globalDefault;
}
/**
 * Calendar cell colour for a date:
 *   - 'red'   : whole day blocked
 *   - 'amber' : one slot blocked OR a capacity-only override exists for the date
 *   - undefined : no overrides (normal)
 */
export function dateOverrideKind(date, slotBlocks) {
    const ds = dateKey(date);
    const rows = slotBlocks.filter((b) => b.date === ds);
    if (rows.length === 0)
        return undefined;
    if (isWholeDayBlocked(date, slotBlocks))
        return "red";
    return "amber";
}
/**
 * Why a date is unavailable for SLOT-LESS scheduling (review dates):
 *   - { kind: "off" }     : the weekday is marked off in the weekly schedule
 *   - { kind: "blocked" } : whole-day blocked (slot_blocks row slot=null with
 *                           max_override 0/NULL — doctor leave/holiday)
 * Returns null when the day is available. `reason` carries the block row's
 * reason text when one exists. Use for review-date pickers; per-slot booking
 * pickers should use isSlotBlocked per slot in addition.
 */
export function dayUnavailableReason(date, weeklySchedule, slotBlocks) {
    if (!isWorkingDay(date, weeklySchedule))
        return { kind: "off", reason: null };
    if (isWholeDayBlocked(date, slotBlocks)) {
        const ds = dateKey(date);
        const row = slotBlocks.find((b) => b.date === ds && b.slot === null);
        return { kind: "blocked", reason: row?.reason ?? null };
    }
    return null;
}
/**
 * Calendar cell status for a date — the single vocabulary every calendar /
 * queue surface uses to mark NO-OPD days:
 *   - 'off'     : the weekday is marked off in the weekly schedule (clinic
 *                 functioning — e.g. Sunday off in Settings → Slot Config)
 *   - 'blocked' : whole day blocked (slot_blocks row slot=null with
 *                 max_override 0/NULL — holiday/doctor leave)
 *   - 'partial' : one slot blocked OR a capacity-only override exists
 *   - 'open'    : normal working day, no overrides
 * `reason` carries the block row's reason text when one exists.
 */
export function calendarDayStatus(date, weeklySchedule, slotBlocks) {
    if (!isWorkingDay(date, weeklySchedule))
        return { kind: "off", reason: null };
    if (isWholeDayBlocked(date, slotBlocks)) {
        const ds = dateKey(date);
        const row = slotBlocks.find((b) => b.date === ds && b.slot === null);
        return { kind: "blocked", reason: row?.reason ?? null };
    }
    if (dateOverrideKind(date, slotBlocks) === "amber") {
        const ds = dateKey(date);
        const row = slotBlocks.find((b) => b.date === ds);
        return { kind: "partial", reason: row?.reason ?? null };
    }
    return { kind: "open", reason: null };
}
/** A date+slot is blocked if a whole-day block exists (max_override 0/NULL)
 * OR a per-slot row matches with max_override = 0. */
export function isSlotBlocked(date, slotKey, slotBlocks) {
    const ds = dateKey(date);
    // Whole-day block (max_override 0 or NULL — legacy rows treated as blocked).
    const wholeDay = slotBlocks.find((b) => b.date === ds && b.slot === null);
    if (wholeDay && (wholeDay.maxOverride === undefined || wholeDay.maxOverride === null || wholeDay.maxOverride === 0)) {
        return true;
    }
    // Per-slot row with max_override = 0.
    const perSlot = slotBlocks.find((b) => b.date === ds && b.slot === slotKey);
    if (perSlot && perSlot.maxOverride === 0)
        return true;
    return false;
}
function dateKey(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
/** 'HH:mm' of `d` in IST (Asia/Kolkata, UTC+5:30) — the clinic's timezone.
 * Uses the Intl API so it is correct regardless of the host timezone. */
function timeKeyIST(d) {
    return new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Asia/Kolkata",
    }).format(d);
}
/**
 * Return the date N days from `fromDate` (inclusive of `fromDate` itself as
 * day 1) that is both a working day and not whole-day-blocked. If the target
 * day is a Sunday or whole-day-blocked, scans forward to the next working day.
 * If no such day within a 365-day window, returns null.
 *
 * Example: nextWorkingDay(Oct 2, 5, ...) targets Oct 6 (Oct 2 = day 1).
 * If Oct 6 is Sunday, returns Oct 7 (the next working day).
 */
export function nextWorkingDay(fromDate, n, weeklySchedule, slotBlocks) {
    const base = new Date(fromDate);
    base.setDate(base.getDate() + n - 1);
    const schedule = normalizeWeeklySchedule(weeklySchedule);
    for (let i = 0; i < 365; i++) {
        const candidate = new Date(base);
        candidate.setDate(base.getDate() + i);
        if (isWorkingDay(candidate, schedule) && !isWholeDayBlocked(candidate, slotBlocks)) {
            return candidate;
        }
    }
    return null;
}
/**
 * Resolve the effective start time (HH:mm) for a slot on a given date:
 *   1. slot_blocks start_override on the date (per-slot row first, then
 *      whole-day row)
 *   2. weeklySchedule[dow] time (legacy morning/evening fields — works for
 *      the v1 keys 'Morning'/'Evening')
 *   3. global fallback (`morningSlotStart`/`eveningSlotStart` in app_settings —
 *      passed in via fallback arg)
 */
export function effectiveSlotStart(date, slot, weeklySchedule, slotBlocks, fallback) {
    const ds = dateKey(date);
    // Per-slot start_override wins, then whole-day start_override.
    const perSlot = slotBlocks.find((b) => b.date === ds && b.slot === slot);
    if (perSlot?.startOverride)
        return perSlot.startOverride;
    const wholeDay = slotBlocks.find((b) => b.date === ds && b.slot === null);
    if (wholeDay?.startOverride)
        return wholeDay.startOverride;
    const day = weeklySchedule?.[weekdayKey(date)];
    if (day && typeof day === "object" && !day.off) {
        return slot === "Morning" ? day.morningStart : day.eveningStart;
    }
    return slot === "Morning" ? fallback.morningStart : fallback.eveningStart;
}
//# sourceMappingURL=scheduling.js.map